import { Button, Checkbox, DatePicker, Drawer, Form, Radio, Segmented, Select, Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import { ApiService } from "api/api.service";
import dayjs from "dayjs";
import { CASE_STATUS, CaseSchemaDto } from "dtos/leadCases.dto";
import { Dispatch, SetStateAction, useState } from "react";
import { TbChevronRight, TbColumns3, TbSearch, TbX } from "react-icons/tb";
import { T_Filters, T_ShowColumns } from "../LeadCases.utils";
import { CASE_STATUS_COLOURS, CASE_STATUS_ICONS } from "utils/constants";

export function AdvancedFiltersDrawer({
  isAdvFiltersDrawerOpen,
  setIsAdvFiltersDrawerOpen,
  filters,
  setFilters,
  paginatedAllUsers,
  batchCases,
  columns,
  showColumns,
  setShowColumns,
}: {
  isAdvFiltersDrawerOpen: boolean;
  setIsAdvFiltersDrawerOpen: (val: boolean) => void;
  filters: T_Filters;
  setFilters: Dispatch<SetStateAction<T_Filters>>;
  paginatedAllUsers: ReturnType<typeof ApiService.user.useGetPaginated>;
  batchCases: ReturnType<typeof ApiService.lead.useGetLeadCases>;
  columns: ColumnsType<CaseSchemaDto>;
  showColumns: T_ShowColumns;
  setShowColumns: Dispatch<SetStateAction<T_ShowColumns>>;
}) {
  const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState(false);

  const [advFiltersForm] = Form.useForm();
  const dateRangeOf = Form.useWatch("rangeOf", advFiltersForm);
  const assignMatch = Form.useWatch("assign_match", advFiltersForm);

  return (
    <Drawer
      destroyOnClose
      width={isColumnDrawerOpen ? 850 : 500}
      title="Advanced Filters"
      open={isAdvFiltersDrawerOpen}
      onClose={() => {
        advFiltersForm.resetFields();
        setIsAdvFiltersDrawerOpen(false);
      }}
      footer={
        <Button
          block
          type="primary"
          onClick={() => {
            advFiltersForm.submit();
          }}
          loading={batchCases.isPending}
        >
          Submit
        </Button>
      }
    >
      <div
        className="flex  overflow-x-hidden"
        // style={{ gridTemplateColumns: `1fr ${isColumnDrawerOpen ? "350px" : "0px"}` }}
      >
        <div className="flex flex-col gap-4 p-5 flex-1">
          <Button icon={<TbColumns3 />} onClick={() => setIsColumnDrawerOpen(!isColumnDrawerOpen)}>
            <span>Columns to Show</span>
            <div className="ml-auto">{isColumnDrawerOpen ? <TbX /> : <TbChevronRight />}</div>
          </Button>
          <Form
            form={advFiltersForm}
            preserve={false}
            initialValues={{
              ...filters,
              rangeStart: filters?.rangeStart ? dayjs(filters?.rangeStart) : undefined,
              rangeEnd: filters?.rangeEnd ? dayjs(filters?.rangeEnd) : undefined,
            }}
            layout="vertical"
            onFinish={(values) => {
              const newVal = structuredClone(values);
              // if (values?.rangeStartEnd) {
              //   newVal.rangeStart = values?.rangeStartEnd?.[0]?.toISOString();
              //   newVal.rangeEnd = values?.rangeStartEnd?.[1]?.toISOString();
              //   delete newVal["rangeStartEnd"];
              // }
              if (values?.rangeStart) {
                newVal.rangeStart = values?.rangeStart?.toISOString();
              }
              if (values?.rangeEnd) {
                newVal.rangeEnd = values?.rangeEnd?.toISOString();
              }
              if (!newVal?.filter?.step) {
                delete newVal?.filter?.["step"];
              }
              setFilters((prev) => ({ ...prev, ...newVal, page: 1 }));
            }}
          >
            <Form.Item<T_Filters> name={"customStatus"} label="Cases">
              <Radio.Group
                options={[
                  { label: "All", value: undefined },
                  { label: "Not Assigned", value: "NOT_ASSIGNED" },
                  { label: "Assigned", value: "ASSIGNED" },
                ]}
                block
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
            <Form.Item<T_Filters> name={"statuses"} label="Status">
              <Checkbox.Group
                className="flex-col gap-2"
                style={{ width: "100%" }}
                options={Object.values(CASE_STATUS).map((el) => ({
                  value: el,
                  label: (
                    <Tag icon={CASE_STATUS_ICONS[el]} color={CASE_STATUS_COLOURS[el]}>
                      {el?.toUpperCase()}
                    </Tag>
                  ),
                }))}
              />
            </Form.Item>
            <Form.Item<T_Filters> name={"assign_match"} label={"Filter by"}>
              <Segmented
                block
                options={[
                  { label: "Assigned to", value: true },
                  { label: "Not Assigned to", value: false },
                ]}
              />
            </Form.Item>
            <Form.Item<T_Filters> name={"assigned_to"} label={`${assignMatch === false ? "Not " : ""}Assigned to`}>
              <Select
                allowClear
                showSearch
                // style={{ width: "200px" }}
                prefix={<TbSearch />}
                placeholder="Search users..."
                options={paginatedAllUsers.data?.result.data?.map((el) => ({ value: el._id, label: el.name }))}
                // defaultOpen
                filterOption={(searchTerm, option) => {
                  return !!option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase());
                }}
              />
            </Form.Item>
            {/* <Form.Item name={["filter", "step"]} label="Step">
					<Radio.Group
						className="flex flex-col gap-2"
						style={{ width: "100%" }}
						options={Object.values(PROCESS_STATUS).map((el) => ({
							value: el,
							label: <Tag>{el?.toUpperCase()}</Tag>,
						}))}
					/>
				</Form.Item> */}
            <Form.Item<T_Filters> name={"rangeOf"} label="Filter date by">
              <Select
                className="flex-col gap-2"
                allowClear
                style={{ width: "100%" }}
                placeholder="Select "
                options={["filedOn", "publishedOn", "createdOn", "updatedOn"].map((el) => ({
                  value: el,
                  label: el,
                }))}
              />
            </Form.Item>
            {dateRangeOf ? (
              <>
                <Form.Item
                  name={"rangeStart"}
                  label="From Date"
                  rules={[
                    ({ getFieldValue }) => {
                      return {
                        validator(_, value) {
                          const rangeEnd = getFieldValue("rangeEnd");
                          if (rangeEnd && dayjs(rangeEnd).isBefore(value)) {
                            return Promise.reject(new Error("Start date should be before end date!"));
                          }
                          return Promise.resolve();
                        },
                      };
                    },
                  ]}
                >
                  <DatePicker className="!w-full" />
                </Form.Item>
                <Form.Item
                  name={"rangeEnd"}
                  label="To Date"
                  rules={[
                    ({ getFieldValue }) => {
                      return {
                        validator(_, value) {
                          const rangeStart = getFieldValue("rangeStart");
                          if (rangeStart && dayjs(rangeStart).isAfter(value)) {
                            return Promise.reject(new Error("End date should be after start date!"));
                          }
                          return Promise.resolve();
                        },
                      };
                    },
                  ]}
                >
                  <DatePicker className="!w-full" />
                </Form.Item>
              </>
            ) : null}
          </Form>
        </div>
        <div className="transition-[width] duration-500" style={{ width: isColumnDrawerOpen ? 300 : 0 }}>
          <div className="flex flex-col gap-3 p-5 bg-gray-100 h-full">
            {columns.map((col) => {
              const colKey = col.key as unknown as keyof typeof showColumns;
              if (colKey !== "actions")
                return (
                  <div key={col.key} className="flex gap-2">
                    <Checkbox
                      checked={showColumns[colKey]}
                      onChange={(e) => {
                        setShowColumns((prev) => {
                          const newVal = { ...prev, [colKey]: e.target.checked };
                          localStorage.setItem("columns", JSON.stringify(newVal));
                          return newVal;
                        });
                      }}
                    >
                      {col.title?.toString()}
                    </Checkbox>
                  </div>
                );
              else return null;
            })}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
